import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Mail, Loader2, FileBarChart, Printer, AlertTriangle } from "lucide-react";
import { adminApi, mondayOf, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtMoney } from "@/components/status";
import ProductionGrid from "./ProductionGrid";
import SapHistory from "./SapHistory";

const DAYS = [["", "All days"], ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

export default function Production() {
  const [mode, setMode] = useState("date");
  const [isd, setIsd] = useState(() => new Date().toISOString().slice(0, 10));
  const [week, setWeek] = useState(mondayOf());
  const [day, setDay] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [histKey, setHistKey] = useState(0);
  const reqRef = useRef(0);

  const args = mode === "date" ? [undefined, undefined, isd] : [week, day || undefined, undefined];
  const load = () => { const id = ++reqRef.current; return adminApi.productionReport(...args).then((d) => { if (id === reqRef.current) setData(d); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, isd, week, day]);

  const email = async () => {
    setBusy(true);
    try {
      const r = await adminApi.emailProduction(...args);
      toast.success(`Emailed to ${(r.to || []).join(", ")} (CC ${(r.cc || []).join(", ")})`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const savePo = async (sec, po) => {
    try { await adminApi.savePoNumber(sec.week_commencing, sec.delivery_day, po); toast.success("PO number saved"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const flagged = (data?.sections || []).reduce((n, s) => n + (s.flag_count || 0), 0);

  return (
    <div data-testid="admin-production">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-[#28282E]">Production Report</h1><p className="text-sm text-[#28282E]/70">Production Totals · live order data · A3 landscape print.</p></div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="open-production-html-button" variant="outline" className="border-slate-200" onClick={() => window.open(adminApi.productionPdfUrl(...args, "html"), "_blank")}><Printer className="mr-2 h-4 w-4" /> Print view</Button>
          <Button data-testid="generate-production-report-pdf-button" variant="outline" className="border-slate-200" onClick={() => window.open(adminApi.productionPdfUrl(...args), "_blank")}><FileDown className="mr-2 h-4 w-4" /> Open PDF (A3)</Button>
          <Button data-testid="email-production-report-metcash-button" onClick={email} disabled={busy} className="bg-[#EBBAD1] text-[#28282E] hover:bg-[#EBBAD1]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Email PDF to Metcash</Button>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-md border border-slate-200 p-0.5 text-sm">
            <button data-testid="scope-date-tab" onClick={() => setMode("date")} className={`rounded px-3 py-1.5 ${mode === "date" ? "bg-[#28282E] text-white" : "text-[#28282E]"}`}>Into-store date</button>
            <button data-testid="scope-week-tab" onClick={() => setMode("week")} className={`rounded px-3 py-1.5 ${mode === "week" ? "bg-[#28282E] text-white" : "text-[#28282E]"}`}>Week + day</button>
          </div>
          {mode === "date" ? (
            <div><Label className="text-xs">Into store date</Label><Input data-testid="report-into-store-input" type="date" value={isd} onChange={(e) => setIsd(e.target.value)} className="mt-1 w-44" /></div>
          ) : (<>
            <div><Label className="text-xs">Week commencing</Label><Input data-testid="report-week-input" type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="mt-1 w-44" /></div>
            <div>
              <Label className="text-xs">Delivery day</Label>
              <select data-testid="report-day-select" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 block w-40 rounded-md border border-slate-200 px-2 py-2 text-sm">
                {DAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </>)}
          {data && (
            <div className="ml-auto flex gap-3">
              {[["Orders", data.order_count], ["Sections", (data.sections || []).length], ["Cartons", Math.round(data.total_cartons)], ["PO total", fmtMoney(data.grand_total)]].map(([l, v]) => (
                <div key={l} data-testid={`stat-${l.toLowerCase().replace(" ", "-")}`} className="rounded-xl bg-[#FBEEF4] px-4 py-2 text-center"><div className="text-xl font-bold text-[#28282E]">{v}</div><div className="text-[11px] text-[#28282E]/70">{l}</div></div>
              ))}
            </div>
          )}
        </div>
        {flagged > 0 && <div data-testid="report-flags-banner" className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> {flagged} product column(s) flagged (price conflict / non-whole cartons / missing product ID). Check red notes under product names.</div>}
      </div>

      {data && (data.sections || []).length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-[#28282E]/55">No orders in this scope.</div>}
      {(data?.sections || []).map((sec) => <ProductionGrid key={sec.into_store_date} section={sec} onSavePo={(po) => savePo(sec, po)} onSapSent={() => { load(); setHistKey((k) => k + 1); }} onChanged={load} />)}

      <SapHistory refreshKey={histKey} />

      {data?.status?.last_emailed_to_metcash_at ? <p className="mt-3 flex items-center gap-1 text-xs text-[#28282E]/70"><FileBarChart className="h-3 w-3" /> Last emailed: {String(data.status.last_emailed_to_metcash_at).slice(0, 16).replace("T", " ")}</p> : null}
    </div>
  );
}
